/* Diente — global utilities: overlays, focus trap, toast, money. */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- money */

  /**
   * Format cents using the shop's money_format, then strip empty decimals so
   * "1.425,00 lei" reads as "1.425" — matching how prices render server-side.
   */
  function formatMoney(cents) {
    var format = window.theme.moneyFormat || '{{amount}}';
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var whole = parts[0];
    var decimal = parts[1];

    function group(str, sep) {
      return str.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + sep);
    }

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, token) {
      switch (token) {
        case 'amount':
          return group(whole, ',') + '.' + decimal;
        case 'amount_no_decimals':
          return group(whole, ',');
        case 'amount_with_comma_separator':
          return group(whole, '.') + ',' + decimal;
        case 'amount_no_decimals_with_comma_separator':
          return group(whole, '.');
        case 'amount_with_apostrophe_separator':
          return group(whole, "'") + '.' + decimal;
        case 'amount_no_decimals_with_space_separator':
          return group(whole, ' ');
        case 'amount_with_space_separator':
          return group(whole, ' ') + ',' + decimal;
        default:
          return '';
      }
    });
  }

  /** Bare number, no currency, no empty decimals — the design's price style. */
  function formatAmount(cents) {
    return formatMoney(cents)
      .replace(/[^\d.,\s']/g, '')
      .trim()
      .replace(/[.,]00$/, '');
  }

  /* -------------------------------------------------------------- overlays */

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var openOverlays = [];
  var lockedScrollY = 0;

  /* Pinning the body is the part `overflow: hidden` cannot do on iOS: without
     it the touch gesture keeps scrolling the page behind an open drawer and the
     drawer's own scroll never receives it. The offset has to be restored by
     hand, because position:fixed drops the page back to the top. */
  function lockScroll() {
    if (document.body.classList.contains('overlay-open')) return;
    lockedScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    document.body.style.top = -lockedScrollY + 'px';
    document.body.classList.add('overlay-open');
  }

  function unlockScroll() {
    if (!document.body.classList.contains('overlay-open')) return;
    document.body.classList.remove('overlay-open');
    document.body.style.top = '';
    window.scrollTo(0, lockedScrollY);
  }

  function trapFocus(container, event) {
    var items = Array.prototype.filter.call(
      container.querySelectorAll(FOCUSABLE),
      function (el) {
        return el.offsetParent !== null;
      }
    );
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function onKeydown(event) {
    if (!openOverlays.length) return;
    var current = openOverlays[openOverlays.length - 1];

    if (event.key === 'Escape') {
      event.preventDefault();
      closeOverlay(current.el);
    } else if (event.key === 'Tab') {
      trapFocus(current.focusRoot, event);
    }
  }

  document.addEventListener('keydown', onKeydown);

  function openOverlay(el, returnFocus) {
    if (!el || el.classList.contains('is-open')) return;

    var focusRoot = el.querySelector('[role="dialog"][aria-modal="true"]') || el;
    openOverlays.push({
      el: el,
      focusRoot: focusRoot,
      returnFocus: returnFocus || document.activeElement
    });
    lockScroll();
    el.classList.add('is-open');
    el.removeAttribute('hidden');

    // Let the element paint before moving focus, or the browser scrolls oddly.
    requestAnimationFrame(function () {
      var target =
        focusRoot.querySelector('[data-autofocus]') || focusRoot.querySelector(FOCUSABLE);
      if (target) target.focus();
    });

    el.dispatchEvent(new CustomEvent('overlay:opened', { bubbles: true }));
  }

  function closeOverlay(el) {
    if (!el || !el.classList.contains('is-open')) return;

    var index = openOverlays.findIndex(function (entry) {
      return entry.el === el;
    });
    var entry = index > -1 ? openOverlays.splice(index, 1)[0] : null;

    el.classList.remove('is-open');
    el.setAttribute('hidden', '');
    if (!openOverlays.length) unlockScroll();
    if (entry && entry.returnFocus && entry.returnFocus.focus) {
      entry.returnFocus.focus({ preventScroll: true });
    }

    el.dispatchEvent(new CustomEvent('overlay:closed', { bubbles: true }));
  }

  function closeAllOverlays() {
    openOverlays.slice().forEach(function (entry) {
      closeOverlay(entry.el);
    });
  }

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-overlay-open]');
    if (opener) {
      event.preventDefault();
      openOverlay(document.getElementById(opener.getAttribute('data-overlay-open')), opener);
      return;
    }

    var closer = event.target.closest('[data-overlay-close]');
    if (closer) {
      event.preventDefault();
      closeOverlay(closer.closest('.overlay'));
    }
  });

  /* ----------------------------------------------------------------- toast */

  var toastTimer = null;
  var announcementTimers = { polite: null, assertive: null };
  var queuedAnnouncementKey = 'diente:announcement';

  function announce(message, priority) {
    if (!message) return;
    var mode = priority === 'assertive' ? 'assertive' : 'polite';
    var region = document.getElementById(
      mode === 'assertive' ? 'DienteAlert' : 'DienteStatus'
    );
    if (!region) return;

    clearTimeout(announcementTimers[mode]);
    region.textContent = '';
    announcementTimers[mode] = setTimeout(function () {
      region.textContent = message;
    }, 50);
  }

  function queueAnnouncement(message, priority) {
    try {
      window.sessionStorage.setItem(
        queuedAnnouncementKey,
        JSON.stringify({ message: message, priority: priority || 'polite' })
      );
    } catch (error) {
      announce(message, priority);
    }
  }

  document.addEventListener('toast:show', function (event) {
    var toast = document.getElementById('Toast');
    var detail = event.detail || {};
    if (!toast) return;

    var text = toast.querySelector('[data-toast-text]');
    if (text) text.textContent = detail.message || '';
    announce(detail.message, detail.priority);

    toast.classList.add('is-open');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toast.classList.remove('is-open');
    }, 3600);
  });

  document.addEventListener('click', function (event) {
    if (!event.target.closest('[data-toast-action]')) return;
    var toast = document.getElementById('Toast');
    if (toast) toast.classList.remove('is-open');
    document.dispatchEvent(new CustomEvent('cart:open'));
  });

  /* ------------------------------------------------------ section rendering */

  /** Fetch one section's HTML via the Section Rendering API. */
  function fetchSection(sectionId, url) {
    var target = new URL(url || window.location.href, window.location.origin);
    target.searchParams.set('section_id', sectionId);

    return fetch(target.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then(
      function (response) {
        if (!response.ok) throw new Error('Section ' + sectionId + ' failed: ' + response.status);
        return response.text();
      }
    );
  }

  /* ------------------------------------------------------------- accordions */

  // Native <details> handles the FAQ accordions; this only enforces
  // "one open at a time" inside groups that ask for it.
  document.addEventListener('click', function (event) {
    var summary = event.target.closest('details.accordion__item > summary');
    if (!summary) return;

    var item = summary.parentElement;
    var group = item.closest('[data-accordion-exclusive]');
    if (!group || item.open) return;

    group.querySelectorAll('details.accordion__item[open]').forEach(function (other) {
      if (other !== item) other.open = false;
    });
  });

  /* ----------------------------------------------------------------- export */

  window.Diente = {
    formatMoney: formatMoney,
    formatAmount: formatAmount,
    openOverlay: openOverlay,
    closeOverlay: closeOverlay,
    closeAllOverlays: closeAllOverlays,
    fetchSection: fetchSection,
    announce: announce,
    queueAnnouncement: queueAnnouncement,
    toast: function (message, priority) {
      document.dispatchEvent(
        new CustomEvent('toast:show', {
          detail: { message: message, priority: priority || 'polite' }
        })
      );
    }
  };

  try {
    var queuedAnnouncement = JSON.parse(
      window.sessionStorage.getItem(queuedAnnouncementKey) || 'null'
    );
    window.sessionStorage.removeItem(queuedAnnouncementKey);
    if (queuedAnnouncement && queuedAnnouncement.message) {
      setTimeout(function () {
        announce(queuedAnnouncement.message, queuedAnnouncement.priority);
      }, 0);
    }
  } catch (error) {
    try {
      window.sessionStorage.removeItem(queuedAnnouncementKey);
    } catch (storageError) {}
  }
})();
